import { useEffect, useMemo, useRef, useState } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/recall-preparedness.ts'
import {
	createContactReferenceDefinition,
	sanitizeContactReferenceDraft,
} from '#app/routes/mocra+/_form+/components/contact-references/definition.ts'
import { ContactOverlay } from '#app/routes/mocra+/_form+/components/contacts/overlay.tsx'
import {
	createEmptyContactDraft,
	type ContactDraft,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { distributionSystemDefinition } from '#app/routes/mocra+/_form+/components/distribution-systems/definition.ts'
import { DistributionSystemEditOverlay } from '#app/routes/mocra+/_form+/components/distribution-systems/edit.tsx'
import {
	createDistributionSystemDraft,
	sanitizeDistributionSystemDraft,
	type DistributionSystemDraft,
	isDistributionSystemValue,
} from '#app/routes/mocra+/_form+/components/distribution-systems/types.ts'
import { RecallEventEditOverlay } from '#app/routes/mocra+/_form+/components/recall-events/edit.tsx'
import { RecallEventEntrySchema } from '#app/routes/mocra+/_form+/components/recall-events/schema.ts'
import {
	createRecallEventDraft,
	enforceMockDefaults,
	normalizeRecallEventDraft,
	sanitizeRecallEventDraft,
	type RecallEventDraft,
	RECALL_EVENT_OUTCOME_FOR_MOCK,
	RECALL_EVENT_TYPE_MOCK,
} from '#app/routes/mocra+/_form+/components/recall-events/types.ts'
import { salesChannelDefinition } from '#app/routes/mocra+/_form+/components/sales-channels/definition.ts'
import { SalesChannelEditOverlay } from '#app/routes/mocra+/_form+/components/sales-channels/edit.tsx'
import {
	createSalesChannelDraft,
	sanitizeSalesChannelDraft,
	type SalesChannelDraft,
	isSalesChannelTypeValue,
} from '#app/routes/mocra+/_form+/components/sales-channels/types.ts'
import {
	contactDraftToReference,
	createEmptyContactReference,
	normalizeContactReference,
	referenceToContactDraft,
	resolveContactDisplay,
	type ContactDisplay,
	type ContactReference,
} from '#app/routes/mocra+/_form+/contacts/reference.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useContactDirectory } from '#app/routes/mocra+/_form+/hooks/use-contact-directory.ts'
import {
	useEntityCollection,
	type EntityCollectionResult,
} from '#app/routes/mocra+/_form+/hooks/use-entity-collection.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import {
	RECALL_DRILL_RECENCY_OPTIONS,
	RECALL_EVENT_OUTCOME_OPTIONS,
	RECALL_EVENT_TYPE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'
import { DistributionSystemsSection } from '#app/routes/mocra+/_form+/recall-preparedness/distribution-systems-section.tsx'
import { DrillCadenceSection } from '#app/routes/mocra+/_form+/recall-preparedness/drill-cadence-section.tsx'
import { IncidentTeamSection } from '#app/routes/mocra+/_form+/recall-preparedness/incident-team-section.tsx'
import { RecallHistorySection } from '#app/routes/mocra+/_form+/recall-preparedness/recall-history-section.tsx'
import { SalesChannelsSection } from '#app/routes/mocra+/_form+/recall-preparedness/sales-channels-section.tsx'
import { createRouteSectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'MoCRA — Recall Preparedness' },
]

const SECTION_KEY = 'recallPreparedness'
const STEP_ID = 'recall-preparedness'
const RECALL_PREPAREDNESS_RESOURCES = createRouteSectionResources({
	route: '/mocra/recall-preparedness',
})

type SalesChannelEntry = RecallPreparednessFormValues['salesChannels'][number]
type DistributionSystemEntry =
	RecallPreparednessFormValues['distributionSystems'][number]

export const RECALL_HISTORY_STATUS_OPTIONS = [
	{ value: 'none', label: 'No recalls or withdrawals yet' },
	{ value: 'mock_only', label: 'Mock drills only' },
	{ value: 'market_withdrawal', label: 'Market withdrawal(s)' },
	{ value: 'recall', label: 'Recall(s) filed with FDA' },
] as const

type RecallHistoryStatusOption = (typeof RECALL_HISTORY_STATUS_OPTIONS)[number]
type RecallHistoryStatusValue = RecallHistoryStatusOption['value']

const RECALL_HISTORY_STATUS_OPTIONS_SET = new Set<RecallHistoryStatusValue>(
	RECALL_HISTORY_STATUS_OPTIONS.map((option) => option.value),
)
const RECALL_DRILL_RECENCY_OPTIONS_SET = new Set<
	(typeof RECALL_DRILL_RECENCY_OPTIONS)[number]['value']
>(RECALL_DRILL_RECENCY_OPTIONS.map((option) => option.value))

const RECALL_HISTORY_STATUS_REQUIRES_EVENTS = new Set<RecallHistoryStatusValue>(
	RECALL_HISTORY_STATUS_OPTIONS.filter((option) => option.value !== 'none').map(
		(option) => option.value,
	),
)

const RECALL_HISTORY_STATUS_MOCK_ONLY = 'mock_only'
const INCIDENT_TEAM_CONFIG = [
	{
		key: 'activation',
		question:
			'Who activates the recall team and coordinates the overall response?',
		description:
			'This person pulls the team together, triggers SOPs, and keeps the recall moving across workstreams.',
		defaultName: 'Activation lead',
		defaultTitle: 'Recall activation lead',
		directorySource: 'Recall team — activation',
	},
	{
		key: 'regulatory',
		question: 'Who manages regulatory filings and FDA communication?',
		description:
			'Owns recall notifications, FDA correspondence, and follow-up reporting.',
		defaultName: 'Regulatory contact',
		defaultTitle: 'Regulatory liaison',
		directorySource: 'Recall team — regulatory',
	},
	{
		key: 'supplyChain',
		question: 'Who leads supply chain holds and lot tracing?',
		description:
			'Coordinates distribution holds, lot identification, and reconciliation tasks.',
		defaultName: 'Supply chain lead',
		defaultTitle: 'Supply chain lead',
		directorySource: 'Recall team — supply chain',
	},
	{
		key: 'manufacturing',
		question: 'Who directs manufacturing and quality remediation?',
		description:
			'Oversees production stops, investigations, and corrective actions on the line.',
		defaultName: 'Manufacturing lead',
		defaultTitle: 'Manufacturing lead',
		directorySource: 'Recall team — manufacturing',
	},
	{
		key: 'communications',
		question: 'Who manages customer and external communications?',
		description:
			'Prepares messaging for customers, retailers, partners, media, and internal support teams.',
		defaultName: 'Communications lead',
		defaultTitle: 'Communications lead',
		directorySource: 'Recall team — communications',
	},
	{
		key: 'customerCare',
		question: 'Who ensures customer support and logistics follow-through?',
		description:
			'Handles inbound support escalations, returns, replacements, and call center updates.',
		defaultName: 'Customer care lead',
		defaultTitle: 'Customer care lead',
		directorySource: 'Recall team — customer care',
	},
	{
		key: 'other',
		question: 'Who covers any other critical recall responsibilities?',
		description:
			'Capture additional niche roles—legal, finance, risk management, or partner coordination.',
		defaultName: 'Recall contact',
		defaultTitle: 'Recall contact',
		directorySource: 'Recall team — other',
	},
] as const

type IncidentTeamStageConfig = (typeof INCIDENT_TEAM_CONFIG)[number]
export type IncidentTeamStageKey = IncidentTeamStageConfig['key']

const INCIDENT_TEAM_DEFINITIONS: Record<
	IncidentTeamStageKey,
	ReturnType<typeof createContactReferenceDefinition>
> = Object.fromEntries(
	INCIDENT_TEAM_CONFIG.map((config) => [
		config.key,
		createContactReferenceDefinition({
			defaultTitle: config.defaultTitle,
		}),
	]),
) as Record<
	IncidentTeamStageKey,
	ReturnType<typeof createContactReferenceDefinition>
>

type IncidentContactCollection = EntityCollectionResult<
	ContactReference,
	ContactDraft
>

const PRIMARY_COORDINATOR_DEFAULT_NAME = 'Primary recall coordinator'
const PRIMARY_COORDINATOR_DEFAULT_TITLE = 'Recall coordinator'
const PRIMARY_COORDINATOR_DIRECTORY_SOURCE = 'Recall primary coordinator'

const IncidentTeamStageSchema = z.array(
	z.object({
		contactId: z.string().min(1),
		fallbackName: z.string().optional(),
		fallbackEmail: z.string().email().optional(),
		fallbackTitle: z.string().optional(),
		fallbackPhone: z.string().optional(),
	}),
)

const IncidentTeamSchema = z.object(
	Object.fromEntries(
		INCIDENT_TEAM_CONFIG.map((config) => [config.key, IncidentTeamStageSchema]),
	) as Record<IncidentTeamStageKey, typeof IncidentTeamStageSchema>,
)

const OptionalContactReferenceSchema = z
	.object({
		contactId: z.string().optional(),
		fallbackName: z.string().optional(),
		fallbackEmail: z.string().email().optional(),
		fallbackTitle: z.string().optional(),
		fallbackPhone: z.string().optional(),
	})
	.transform((reference) => {
		const result: ContactReference = {
			contactId: reference.contactId?.trim() ?? '',
		}
		if (reference.fallbackName) result.fallbackName = reference.fallbackName
		if (reference.fallbackEmail) result.fallbackEmail = reference.fallbackEmail
		if (reference.fallbackTitle) result.fallbackTitle = reference.fallbackTitle
		if (reference.fallbackPhone) result.fallbackPhone = reference.fallbackPhone
		return result
	})

const RecallEventSchema = RecallEventEntrySchema

const RecallHistorySchema = z
	.object({
		status: z.enum(
			RECALL_HISTORY_STATUS_OPTIONS.map((option) => option.value) as [
				(typeof RECALL_HISTORY_STATUS_OPTIONS)[number]['value'],
				...string[],
			],
		),
		events: z.array(RecallEventSchema).default([]),
	})
	.superRefine((value, ctx) => {
		const events = value.events
		const requiresEvents = RECALL_HISTORY_STATUS_REQUIRES_EVENTS.has(
			value.status,
		)
		const hasEvents = events.length > 0

		if (!requiresEvents && hasEvents) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Remove events when you have no recall or withdrawal history.',
				path: ['events'],
			})
		}

		if (value.status === RECALL_HISTORY_STATUS_MOCK_ONLY) {
			events.forEach((event, index) => {
				if (event.type !== RECALL_EVENT_TYPE_MOCK) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Only mock drills are allowed for this history status.',
						path: ['events', index, 'type'],
					})
				}
			})
		}
	})

const RecallPreparednessSchema = z.object({
	salesChannels: z
		.array(
			z.object({
				type: z.string(),
				name: z.string(),
			}),
		)
		.default([]),
	distributionSystems: z
		.array(
			z.object({
				system: z.string(),
				dataset: z.string(),
			}),
		)
		.default([]),
	incidentTeam: IncidentTeamSchema,
	primaryCoordinator: OptionalContactReferenceSchema,
	drillRecency: z.enum(
		RECALL_DRILL_RECENCY_OPTIONS.map((option) => option.value) as [
			(typeof RECALL_DRILL_RECENCY_OPTIONS)[number]['value'],
			...string[],
		],
	),
	recallHistory: RecallHistorySchema,
})

export type RecallPreparednessFormValues = z.infer<
	typeof RecallPreparednessSchema
>

type RecallEventEntry = z.infer<typeof RecallEventSchema>
type IncidentTeamValues = RecallPreparednessFormValues['incidentTeam']

const DEFAULT_VALUES: RecallPreparednessFormValues = {
	salesChannels: [],
	distributionSystems: [],
	incidentTeam: createEmptyIncidentTeam(),
	primaryCoordinator: createEmptyContactReference(),
	drillRecency: RECALL_DRILL_RECENCY_OPTIONS[0]?.value ?? 'within_6_months',
	recallHistory: {
		status: RECALL_HISTORY_STATUS_OPTIONS[0]?.value ?? 'none',
		events: [],
	},
}

function createEmptyIncidentTeam(): IncidentTeamValues {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const base: IncidentTeamValues = {} as IncidentTeamValues
	for (const config of INCIDENT_TEAM_CONFIG) {
		base[config.key] = []
	}
	return base
}

function prepareRecallEventForStatus(
	status: RecallPreparednessFormValues['recallHistory']['status'],
	draft: RecallEventDraft,
): RecallEventDraft {
	const sanitized = sanitizeRecallEventDraft(draft)
	return status === RECALL_HISTORY_STATUS_MOCK_ONLY
		? enforceMockDefaults(sanitized)
		: sanitized
}

export default function RecallPreparedness() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const form = useSectionForm({
		section: SECTION_KEY,
		schema: RecallPreparednessSchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})

	const {
		values,
		setValues,
		setFieldValue,
		getFieldErrors,
		submit,
		status,
		hasStoredValue,
		clear,
	} = form

	const didNormalizeRef = useRef(false)
	useEffect(() => {
		if (didNormalizeRef.current) return
		const normalized = normalizeRecallPreparednessDraft(values)
		if (normalized && !recallPreparednessValuesEqual(normalized, values)) {
			didNormalizeRef.current = true
			setValues(normalized)
			return
		}
		didNormalizeRef.current = true
		if (!normalized && !recallPreparednessValuesEqual(DEFAULT_VALUES, values)) {
			setValues(DEFAULT_VALUES)
		}
	}, [setValues, values])

	const {
		contacts: directoryContacts,
		candidates: directoryCandidates,
		mergeContacts,
		upsertContact: upsertDirectoryContact,
	} = useContactDirectory()

	const directoryMap = useMemo(() => {
		const map = new Map<string, (typeof directoryContacts)[number]>()
		for (const entry of directoryContacts) {
			map.set(entry.id, entry)
		}
		return map
	}, [directoryContacts])

	const salesChannelsCollection = useEntityCollection<
		RecallPreparednessFormValues,
		SalesChannelEntry,
		SalesChannelDraft
	>({
		form,
		fieldPath: 'salesChannels',
		definition: salesChannelDefinition,
		getFieldErrorPaths: getSalesChannelFieldErrorPaths,
	})

	const distributionSystemsCollection = useEntityCollection<
		RecallPreparednessFormValues,
		DistributionSystemEntry,
		DistributionSystemDraft
	>({
		form,
		fieldPath: 'distributionSystems',
		definition: distributionSystemDefinition,
		getFieldErrorPaths: getDistributionSystemFieldErrorPaths,
	})

	const incidentActivationCollection = useEntityCollection<
		RecallPreparednessFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'incidentTeam.activation',
		definition: INCIDENT_TEAM_DEFINITIONS.activation,
		getFieldErrorPaths: (index) =>
			getIncidentTeamFieldErrorPaths('activation', index),
	})

	const incidentRegulatoryCollection = useEntityCollection<
		RecallPreparednessFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'incidentTeam.regulatory',
		definition: INCIDENT_TEAM_DEFINITIONS.regulatory,
		getFieldErrorPaths: (index) =>
			getIncidentTeamFieldErrorPaths('regulatory', index),
	})

	const incidentSupplyChainCollection = useEntityCollection<
		RecallPreparednessFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'incidentTeam.supplyChain',
		definition: INCIDENT_TEAM_DEFINITIONS.supplyChain,
		getFieldErrorPaths: (index) =>
			getIncidentTeamFieldErrorPaths('supplyChain', index),
	})

	const incidentManufacturingCollection = useEntityCollection<
		RecallPreparednessFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'incidentTeam.manufacturing',
		definition: INCIDENT_TEAM_DEFINITIONS.manufacturing,
		getFieldErrorPaths: (index) =>
			getIncidentTeamFieldErrorPaths('manufacturing', index),
	})

	const incidentCommunicationsCollection = useEntityCollection<
		RecallPreparednessFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'incidentTeam.communications',
		definition: INCIDENT_TEAM_DEFINITIONS.communications,
		getFieldErrorPaths: (index) =>
			getIncidentTeamFieldErrorPaths('communications', index),
	})

	const incidentCustomerCareCollection = useEntityCollection<
		RecallPreparednessFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'incidentTeam.customerCare',
		definition: INCIDENT_TEAM_DEFINITIONS.customerCare,
		getFieldErrorPaths: (index) =>
			getIncidentTeamFieldErrorPaths('customerCare', index),
	})

	const incidentOtherCollection = useEntityCollection<
		RecallPreparednessFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'incidentTeam.other',
		definition: INCIDENT_TEAM_DEFINITIONS.other,
		getFieldErrorPaths: (index) =>
			getIncidentTeamFieldErrorPaths('other', index),
	})

	const incidentCollectionByKey: Record<
		IncidentTeamStageKey,
		IncidentContactCollection
	> = {
		activation: incidentActivationCollection,
		regulatory: incidentRegulatoryCollection,
		supplyChain: incidentSupplyChainCollection,
		manufacturing: incidentManufacturingCollection,
		communications: incidentCommunicationsCollection,
		customerCare: incidentCustomerCareCollection,
		other: incidentOtherCollection,
	}

	const incidentStageCollections = INCIDENT_TEAM_CONFIG.map((config) => ({
		config,
		collection: incidentCollectionByKey[config.key],
	}))

	const recallHistory = ensureRecallHistory(values.recallHistory)

	const recallEventTypeOptions = useMemo(() => {
		if (recallHistory.status === RECALL_HISTORY_STATUS_MOCK_ONLY) {
			return RECALL_EVENT_TYPE_OPTIONS.filter(
				(option) => option.value === RECALL_EVENT_TYPE_MOCK,
			)
		}
		return RECALL_EVENT_TYPE_OPTIONS
	}, [recallHistory.status])

	const recallEventOutcomeOptions = useMemo(() => {
		if (recallHistory.status === RECALL_HISTORY_STATUS_MOCK_ONLY) {
			return RECALL_EVENT_OUTCOME_OPTIONS.filter(
				(option) => option.value === RECALL_EVENT_OUTCOME_FOR_MOCK,
			)
		}
		return RECALL_EVENT_OUTCOME_OPTIONS
	}, [recallHistory.status])

	const recallEventDefaultDraft = useMemo(
		() =>
			prepareRecallEventForStatus(
				recallHistory.status,
				createRecallEventDraft(),
			),
		[recallHistory.status],
	)

	const salesChannelItems = salesChannelsCollection.items.map(
		({ draft, errors }) => ({
			draft,
			errors,
		}),
	)

	const distributionSystemItems = distributionSystemsCollection.items.map(
		({ draft, errors }) => ({
			draft,
			errors,
		}),
	)

	const stageConfigMap = useMemo(() => {
		const map = new Map<IncidentTeamStageKey, IncidentTeamStageConfig>()
		for (const config of INCIDENT_TEAM_CONFIG) {
			map.set(config.key, config)
		}
		return map
	}, [])

	const incidentStageViews = useMemo(() => {
		return incidentStageCollections.map(({ config, collection }) => {
			const displays = collection.items.map((item) =>
				resolveContactDisplay(item.entry, directoryMap, {
					defaultName: config.defaultName,
					defaultTitle: config.defaultTitle,
				}),
			)
			const contacts = collection.items.map((item, index) => ({
				display: displays[index] ?? {
					contactId: item.entry.contactId ?? '',
					name: config.defaultName,
					email: item.entry.fallbackEmail ?? '',
					title: config.defaultTitle,
					phone: item.entry.fallbackPhone ?? '',
				},
				errors: item.errors,
			}))
			const existingContacts = collection.items.map((item) =>
				collection.sanitizeDraft(item.draft),
			)
			const listErrors = getFieldErrors(`incidentTeam.${config.key}`)
			return {
				config,
				contacts,
				existingContacts,
				listErrors,
			}
		})
	}, [directoryMap, getFieldErrors, incidentStageCollections])

	const incidentStageViewsForComponent = useMemo(
		() =>
			incidentStageViews.map((stage) => ({
				key: stage.config.key,
				question: stage.config.question,
				description: stage.config.description,
				defaultName: stage.config.defaultName,
				defaultTitle: stage.config.defaultTitle,
				directorySource: stage.config.directorySource,
				contacts: stage.contacts,
				existingContacts: stage.existingContacts,
				listErrors: stage.listErrors,
			})),
		[incidentStageViews],
	)

	const incidentStageDraftSignature = useMemo(
		() =>
			incidentStageViews
				.map((stage) =>
					stage.existingContacts
						.map((contact) =>
							[
								contact.id ?? '',
								contact.email,
								contact.name,
								contact.phone,
							].join('|'),
						)
						.join('::'),
				)
				.join(';;'),
		[incidentStageViews],
	)
	const incidentStageMergeSignatureRef = useRef<string | null>(null)

	const [editingSalesChannelIndex, setEditingSalesChannelIndex] = useState<
		number | null
	>(null)
	const [editingDistributionSystemIndex, setEditingDistributionSystemIndex] =
		useState<number | null>(null)
	const [editingIncidentContact, setEditingIncidentContact] = useState<{
		stage: IncidentTeamStageKey
		index: number
	} | null>(null)
	const [editingPrimaryCoordinator, setEditingPrimaryCoordinator] =
		useState(false)
	const [isRecallEventEditorOpen, setIsRecallEventEditorOpen] = useState(false)
	const [editingRecallEventIndex, setEditingRecallEventIndex] = useState<
		number | null
	>(null)

	const editingSalesChannelDraft =
		editingSalesChannelIndex !== null &&
		salesChannelsCollection.items[editingSalesChannelIndex]
			? salesChannelsCollection.items[editingSalesChannelIndex].draft
			: salesChannelsCollection.createDraft()

	const editingDistributionSystemDraft =
		editingDistributionSystemIndex !== null &&
		distributionSystemsCollection.items[editingDistributionSystemIndex]
			? distributionSystemsCollection.items[editingDistributionSystemIndex]
					.draft
			: distributionSystemsCollection.createDraft()

	const primaryCoordinatorReference = normalizeContactReference(
		values.primaryCoordinator ?? createEmptyContactReference(),
	)
	const primaryCoordinatorHasContact =
		primaryCoordinatorReference.contactId.trim().length > 0
	const primaryCoordinatorDisplay: ContactDisplay = primaryCoordinatorHasContact
		? resolveContactDisplay(primaryCoordinatorReference, directoryMap, {
				defaultName: PRIMARY_COORDINATOR_DEFAULT_NAME,
				defaultTitle: PRIMARY_COORDINATOR_DEFAULT_TITLE,
			})
		: {
				contactId: '',
				name: PRIMARY_COORDINATOR_DEFAULT_NAME,
				email: '',
				title: PRIMARY_COORDINATOR_DEFAULT_TITLE,
				phone: '',
			}
	const primaryCoordinatorDraft = primaryCoordinatorHasContact
		? referenceToContactDraft(
				primaryCoordinatorReference,
				primaryCoordinatorDisplay,
			)
		: createEmptyContactDraft()
	const primaryCoordinatorErrors = getFieldErrors(
		'primaryCoordinator.contactId',
	)
	const primaryCoordinatorDraftSignature = [
		primaryCoordinatorDraft.id ?? '',
		primaryCoordinatorDraft.email,
		primaryCoordinatorDraft.name,
		primaryCoordinatorDraft.phone,
	].join('|')
	const primaryCoordinatorMergeSignatureRef = useRef<string | null>(null)

	useEffect(() => {
		const signature = incidentStageDraftSignature
		if (!signature) {
			incidentStageMergeSignatureRef.current = null
			return
		}
		if (incidentStageMergeSignatureRef.current === signature) return
		incidentStageMergeSignatureRef.current = signature
		for (const stage of incidentStageViews) {
			if (stage.existingContacts.length === 0) continue
			mergeContacts(stage.existingContacts, {
				source: stage.config.directorySource,
			})
		}
	}, [incidentStageViews, mergeContacts, incidentStageDraftSignature])
	useEffect(() => {
		if (!primaryCoordinatorHasContact || !primaryCoordinatorDraft.id) {
			primaryCoordinatorMergeSignatureRef.current = null
			return
		}
		const signature = primaryCoordinatorDraftSignature
		if (primaryCoordinatorMergeSignatureRef.current === signature) return
		primaryCoordinatorMergeSignatureRef.current = signature
		if (primaryCoordinatorHasContact && primaryCoordinatorDraft.id) {
			mergeContacts([primaryCoordinatorDraft], {
				source: PRIMARY_COORDINATOR_DIRECTORY_SOURCE,
			})
		}
	}, [
		mergeContacts,
		primaryCoordinatorHasContact,
		primaryCoordinatorDraft,
		primaryCoordinatorDraftSignature,
	])

	const handleAddSalesChannel = (draft: SalesChannelDraft) => {
		salesChannelsCollection.append(draft)
	}

	const handleEditSalesChannel = (draft: SalesChannelDraft) => {
		if (editingSalesChannelIndex === null) return
		salesChannelsCollection.replace(editingSalesChannelIndex, draft)
		setEditingSalesChannelIndex(null)
	}

	const handleRemoveSalesChannel = (index: number) => {
		salesChannelsCollection.remove(index)
		setEditingSalesChannelIndex(null)
	}

	const handleAddDistributionSystem = (draft: DistributionSystemDraft) => {
		distributionSystemsCollection.append(draft)
	}

	const handleEditDistributionSystem = (draft: DistributionSystemDraft) => {
		if (editingDistributionSystemIndex === null) return
		distributionSystemsCollection.replace(editingDistributionSystemIndex, draft)
		setEditingDistributionSystemIndex(null)
	}

	const handleRemoveDistributionSystem = (index: number) => {
		distributionSystemsCollection.remove(index)
		setEditingDistributionSystemIndex(null)
	}

	const handleAddIncidentContact = (
		stage: IncidentTeamStageKey,
		draft: ContactDraft,
	) => {
		const config = stageConfigMap.get(stage)
		const collection = incidentCollectionByKey[stage]
		if (!config || !collection) return
		const sanitized = sanitizeContactReferenceDraft(draft, config.defaultTitle)
		const contactId = sanitized.id?.trim()
		if (!contactId) return
		if (collection.entries.some((entry) => entry.contactId === contactId))
			return
		collection.append(sanitized)
	}

	const handleRemoveIncidentContact = (
		stage: IncidentTeamStageKey,
		index: number,
	) => {
		const collection = incidentCollectionByKey[stage]
		if (!collection) return
		const removedEntry = collection.items[index]?.entry
		collection.remove(index)
		if (
			removedEntry?.contactId &&
			primaryCoordinatorReference.contactId &&
			removedEntry.contactId === primaryCoordinatorReference.contactId
		) {
			setFieldValue('primaryCoordinator', createEmptyContactReference())
		}
		if (editingIncidentContact?.stage === stage) {
			if (editingIncidentContact.index === index) {
				setEditingIncidentContact(null)
			} else if (editingIncidentContact.index > index) {
				setEditingIncidentContact({
					stage,
					index: editingIncidentContact.index - 1,
				})
			}
		}
	}

	const handleAssignIncidentContact = (draft: ContactDraft) => {
		if (!editingIncidentContact) return
		const { stage, index } = editingIncidentContact
		const config = stageConfigMap.get(stage)
		const collection = incidentCollectionByKey[stage]
		if (!config || !collection) return
		const entry = upsertDirectoryContact(draft, {
			source: config.directorySource,
		})
		const resolvedDraft = sanitizeContactReferenceDraft(
			{
				id: entry?.id ?? draft.id,
				title: entry?.title ?? draft.title,
				name: entry?.name ?? draft.name,
				email: entry?.email ?? draft.email,
				phone: entry?.phone ?? draft.phone,
			},
			config.defaultTitle,
		)
		if (!resolvedDraft.id) return
		const previousEntry = collection.items[index]?.entry
		collection.replace(index, resolvedDraft)
		if (
			previousEntry?.contactId &&
			primaryCoordinatorReference.contactId &&
			previousEntry.contactId === primaryCoordinatorReference.contactId
		) {
			setFieldValue(
				'primaryCoordinator',
				contactDraftToReference(resolvedDraft, {
					defaultTitle: config.defaultTitle,
				}),
			)
		}
		setEditingIncidentContact(null)
	}

	const handleAddPrimaryCoordinator = (draft: ContactDraft) => {
		if (!draft.id) return
		const sanitized = sanitizeContactReferenceDraft(
			draft,
			PRIMARY_COORDINATOR_DEFAULT_TITLE,
		)
		const reference = contactDraftToReference(sanitized, {
			defaultTitle: PRIMARY_COORDINATOR_DEFAULT_TITLE,
		})
		setFieldValue('primaryCoordinator', reference)
	}

	const handleEditPrimaryCoordinator = (draft: ContactDraft) => {
		const entry = upsertDirectoryContact(draft, {
			source: PRIMARY_COORDINATOR_DIRECTORY_SOURCE,
		})
		const resolvedDraft = sanitizeContactReferenceDraft(
			{
				id: entry?.id ?? draft.id,
				title: entry?.title ?? draft.title,
				name: entry?.name ?? draft.name,
				email: entry?.email ?? draft.email,
				phone: entry?.phone ?? draft.phone,
			},
			PRIMARY_COORDINATOR_DEFAULT_TITLE,
		)
		if (!resolvedDraft.id) return
		const reference = contactDraftToReference(resolvedDraft, {
			defaultTitle: PRIMARY_COORDINATOR_DEFAULT_TITLE,
		})
		setFieldValue('primaryCoordinator', reference)
		setEditingPrimaryCoordinator(false)
	}

	const handleClearPrimaryCoordinator = () => {
		setFieldValue('primaryCoordinator', createEmptyContactReference())
	}

	const handleDrillRecencyChange = (
		value: RecallPreparednessFormValues['drillRecency'],
	) => {
		setFieldValue('drillRecency', value)
	}

	const handleHistoryStatusChange = (
		value: RecallPreparednessFormValues['recallHistory']['status'],
	) => {
		setValues((previous) => {
			const history = ensureRecallHistory(previous.recallHistory)
			if (value === 'none') {
				return {
					...previous,
					recallHistory: {
						status: value,
						events: [],
					},
				}
			}
			const events =
				value === RECALL_HISTORY_STATUS_MOCK_ONLY
					? history.events.map((event) =>
							prepareRecallEventForStatus(
								value,
								sanitizeRecallEventDraft(event as RecallEventDraft),
							),
						)
					: history.events.map((event) =>
							sanitizeRecallEventDraft(event as RecallEventDraft),
						)
			return {
				...previous,
				recallHistory: {
					status: value,
					events,
				},
			}
		})
	}

	const handleCreateRecallEvent = (draft: RecallEventDraft) => {
		setValues((previous) => {
			const history = ensureRecallHistory(previous.recallHistory)
			const prepared = prepareRecallEventForStatus(history.status, draft)
			return {
				...previous,
				recallHistory: {
					status: history.status,
					events: [...history.events, prepared],
				},
			}
		})
	}

	const handleRemoveRecallEvent = (index: number) => {
		setValues((previous) => {
			const history = ensureRecallHistory(previous.recallHistory)
			return {
				...previous,
				recallHistory: {
					status: history.status,
					events: history.events.filter((_, idx) => idx !== index),
				},
			}
		})
	}

	const handleRecallEventEditRequest = (index: number) => {
		setEditingRecallEventIndex(index)
		setIsRecallEventEditorOpen(true)
	}

	const handleRecallEventEditSave = (draft: RecallEventDraft) => {
		setValues((previous) => {
			const history = ensureRecallHistory(previous.recallHistory)
			if (editingRecallEventIndex === null) return previous
			const prepared = prepareRecallEventForStatus(history.status, draft)
			const events = history.events.map((event, idx) =>
				idx === editingRecallEventIndex ? prepared : event,
			)
			return {
				...previous,
				recallHistory: {
					status: history.status,
					events,
				},
			}
		})
		setIsRecallEventEditorOpen(false)
		setEditingRecallEventIndex(null)
	}

	const incidentStageForEditing = editingIncidentContact
		? incidentStageCollections.find(
				(stage) => stage.config.key === editingIncidentContact.stage,
			)
		: null

	const incidentEditingDraft =
		editingIncidentContact && incidentStageForEditing
			? incidentStageForEditing.collection.sanitizeDraft(
					incidentStageForEditing.collection.items[editingIncidentContact.index]
						?.draft ?? incidentStageForEditing.collection.createDraft(),
				)
			: createEmptyContactDraft()

	const incidentEditingExcludedEmails =
		editingIncidentContact && incidentStageForEditing
			? incidentStageForEditing.collection.items
					.filter((_, idx) => idx !== editingIncidentContact.index)
					.map((item) =>
						incidentStageForEditing.collection
							.sanitizeDraft(item.draft)
							.email.trim(),
					)
					.filter((email) => email.length > 0)
			: []

	const recallEventEditingDraft =
		editingRecallEventIndex !== null &&
		editingRecallEventIndex >= 0 &&
		editingRecallEventIndex < recallHistory.events.length
			? prepareRecallEventForStatus(
					recallHistory.status,
					sanitizeRecallEventDraft(
						recallHistory.events[editingRecallEventIndex] as RecallEventDraft,
					),
				)
			: recallEventDefaultDraft

	const showRecallEvents = RECALL_HISTORY_STATUS_REQUIRES_EVENTS.has(
		recallHistory.status,
	)

	useEffect(() => {
		if (!showRecallEvents && isRecallEventEditorOpen) {
			setIsRecallEventEditorOpen(false)
			setEditingRecallEventIndex(null)
		}
	}, [showRecallEvents, isRecallEventEditorOpen])

	useEffect(() => {
		if (
			isRecallEventEditorOpen &&
			(editingRecallEventIndex === null ||
				editingRecallEventIndex >= recallHistory.events.length)
		) {
			setIsRecallEventEditorOpen(false)
			setEditingRecallEventIndex(null)
		}
	}, [
		editingRecallEventIndex,
		isRecallEventEditorOpen,
		recallHistory.events.length,
	])

	const historyStatusErrors = getFieldErrors('recallHistory.status')
	const historyEventsError = getFieldErrors('recallHistory.events')[0] ?? null

	const recallHistoryItems = recallHistory.events.map((event, index) => {
		const draft = sanitizeRecallEventDraft(event as RecallEventDraft)
		const errorMessages = collectUniqueErrors([
			...getFieldErrors(`recallHistory.events.${index}.type`),
			...getFieldErrors(`recallHistory.events.${index}.year`),
			...getFieldErrors(`recallHistory.events.${index}.product`),
			...getFieldErrors(`recallHistory.events.${index}.outcome`),
		])
		return {
			draft,
			errors: errorMessages,
		}
	})

	return (
		<Page
			title="Recall Preparedness"
			description="Map the channels and systems impacted by a recall, the team that runs the playbook, and how often you rehearse mock events."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event) => {
					event.preventDefault()
					void submit()
				}}
			>
				<Divider />

				<SalesChannelsSection
					resources={RECALL_PREPAREDNESS_RESOURCES}
					channels={salesChannelItems}
					onAdd={handleAddSalesChannel}
					onEdit={(index) => setEditingSalesChannelIndex(index)}
					onRemove={handleRemoveSalesChannel}
				/>
				<SalesChannelEditOverlay
					open={editingSalesChannelIndex !== null}
					onOpenChange={(open) => {
						if (!open) setEditingSalesChannelIndex(null)
					}}
					onSubmit={handleEditSalesChannel}
					initialValue={editingSalesChannelDraft}
					onClosed={() => setEditingSalesChannelIndex(null)}
				/>

				<Divider />

				<DistributionSystemsSection
					resources={RECALL_PREPAREDNESS_RESOURCES}
					systems={distributionSystemItems}
					onAdd={handleAddDistributionSystem}
					onEdit={(index) => setEditingDistributionSystemIndex(index)}
					onRemove={handleRemoveDistributionSystem}
				/>
				<DistributionSystemEditOverlay
					open={editingDistributionSystemIndex !== null}
					onOpenChange={(open) => {
						if (!open) setEditingDistributionSystemIndex(null)
					}}
					onSubmit={handleEditDistributionSystem}
					initialValue={editingDistributionSystemDraft}
					onClosed={() => setEditingDistributionSystemIndex(null)}
				/>

				<Divider />

				<IncidentTeamSection
					resources={RECALL_PREPAREDNESS_RESOURCES}
					stages={incidentStageViewsForComponent}
					onAddContact={handleAddIncidentContact}
					onEditContact={(stage, index) =>
						setEditingIncidentContact({ stage, index })
					}
					onRemoveContact={handleRemoveIncidentContact}
					onAddPrimaryCoordinator={handleAddPrimaryCoordinator}
					onEditPrimaryCoordinator={() => setEditingPrimaryCoordinator(true)}
					onClearPrimaryCoordinator={handleClearPrimaryCoordinator}
					primaryCoordinator={{
						display: primaryCoordinatorHasContact
							? primaryCoordinatorDisplay
							: null,
						errors: primaryCoordinatorErrors,
						hasCoordinator: primaryCoordinatorHasContact,
					}}
					primaryCoordinatorDirectorySource={
						PRIMARY_COORDINATOR_DIRECTORY_SOURCE
					}
				/>
				<ContactOverlay
					mode="edit"
					open={editingIncidentContact !== null}
					onOpenChange={(open) => {
						if (!open) setEditingIncidentContact(null)
					}}
					onSubmit={handleAssignIncidentContact}
					initialValue={
						editingIncidentContact ? incidentEditingDraft : undefined
					}
					candidates={directoryCandidates}
					excludedEmails={incidentEditingExcludedEmails}
					onClosed={() => setEditingIncidentContact(null)}
					onCancel={() => setEditingIncidentContact(null)}
					submitLabel="Save"
				/>
				<ContactOverlay
					mode="edit"
					open={editingPrimaryCoordinator}
					onOpenChange={(open) => {
						if (!open) setEditingPrimaryCoordinator(false)
					}}
					onSubmit={handleEditPrimaryCoordinator}
					initialValue={primaryCoordinatorDraft}
					candidates={directoryCandidates}
					onClosed={() => setEditingPrimaryCoordinator(false)}
					onCancel={() => setEditingPrimaryCoordinator(false)}
					submitLabel="Save"
				/>

				<Divider />

				<DrillCadenceSection
					resources={RECALL_PREPAREDNESS_RESOURCES}
					value={values.drillRecency}
					errors={getFieldErrors('drillRecency')}
					onChange={handleDrillRecencyChange}
				/>

				<Divider />

				<RecallHistorySection
					resources={RECALL_PREPAREDNESS_RESOURCES}
					statusOptions={RECALL_HISTORY_STATUS_OPTIONS}
					eventTypeOptions={recallEventTypeOptions}
					eventOutcomeOptions={recallEventOutcomeOptions}
					status={recallHistory.status}
					statusErrors={historyStatusErrors}
					eventsError={historyEventsError}
					events={recallHistoryItems}
					showEvents={showRecallEvents}
					onStatusChange={handleHistoryStatusChange}
					onCreateEvent={handleCreateRecallEvent}
					onEditEvent={handleRecallEventEditRequest}
					onRemoveEvent={handleRemoveRecallEvent}
					defaultEventDraft={recallEventDefaultDraft}
				/>
				<RecallEventEditOverlay
					open={isRecallEventEditorOpen}
					onOpenChange={(open) => {
						setIsRecallEventEditorOpen(open)
						if (!open) {
							setEditingRecallEventIndex(null)
						}
					}}
					onSubmit={handleRecallEventEditSave}
					initialValue={recallEventEditingDraft}
					typeOptions={recallEventTypeOptions}
					outcomeOptions={recallEventOutcomeOptions}
					onClosed={() => setEditingRecallEventIndex(null)}
				/>

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}

				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}

function getSalesChannelFieldErrorPaths(index: number): readonly string[] {
	const prefix = `salesChannels.${index}`
	return [prefix, `${prefix}.type`, `${prefix}.name`]
}

function getDistributionSystemFieldErrorPaths(
	index: number,
): readonly string[] {
	const prefix = `distributionSystems.${index}`
	return [prefix, `${prefix}.system`, `${prefix}.dataset`]
}

function getIncidentTeamFieldErrorPaths(
	stage: IncidentTeamStageKey,
	index: number,
): readonly string[] {
	const prefix = `incidentTeam.${stage}.${index}`
	return [prefix, `${prefix}.contactId`]
}

function collectUniqueErrors(errors: readonly string[]): string[] {
	return Array.from(new Set(errors.filter(Boolean)))
}

function salesChannelDraftToEntry(
	draft: SalesChannelDraft,
): RecallPreparednessFormValues['salesChannels'][number] {
	const sanitized = sanitizeSalesChannelDraft(draft)
	return {
		type: sanitized.type,
		name: sanitized.name,
	}
}

function distributionSystemDraftToEntry(
	draft: DistributionSystemDraft,
): RecallPreparednessFormValues['distributionSystems'][number] {
	const sanitized = sanitizeDistributionSystemDraft(draft)
	return {
		system: sanitized.system,
		dataset: sanitized.dataset,
	}
}

function ignoredSalesChannelEntryToDraft(
	entry: RecallPreparednessFormValues['salesChannels'][number],
): SalesChannelDraft {
	const type = isSalesChannelTypeValue(entry.type) ? entry.type : undefined
	return sanitizeSalesChannelDraft(
		createSalesChannelDraft({
			type,
			name: entry.name,
		}),
	)
}

function ignoredDistributionSystemEntryToDraft(
	entry: RecallPreparednessFormValues['distributionSystems'][number],
): DistributionSystemDraft {
	const system = isDistributionSystemValue(entry.system)
		? entry.system
		: undefined
	return sanitizeDistributionSystemDraft(
		createDistributionSystemDraft({
			system,
			dataset: entry.dataset,
		}),
	)
}

function ensureIncidentTeam(
	value: RecallPreparednessFormValues['incidentTeam'] | undefined,
): IncidentTeamValues {
	const base = createEmptyIncidentTeam()
	if (!value || typeof value !== 'object') return base
	for (const config of INCIDENT_TEAM_CONFIG) {
		const raw = (value as Record<string, unknown>)[config.key]
		if (!Array.isArray(raw)) continue
		base[config.key] = raw
			.map((entry) => normalizeIncidentContact(entry))
			.filter(
				(reference): reference is ContactReference =>
					reference !== null && reference.contactId.length > 0,
			)
	}
	return base
}

function normalizeIncidentContact(input: unknown): ContactReference | null {
	if (!input || typeof input !== 'object') return null
	const record = input as Record<string, unknown>
	const contactId =
		typeof record.contactId === 'string' ? record.contactId.trim() : ''
	if (contactId.length === 0) return null
	return normalizeContactReference({
		contactId,
		fallbackName:
			typeof record.fallbackName === 'string' ? record.fallbackName : undefined,
		fallbackEmail:
			typeof record.fallbackEmail === 'string'
				? record.fallbackEmail
				: undefined,
		fallbackTitle:
			typeof record.fallbackTitle === 'string'
				? record.fallbackTitle
				: undefined,
		fallbackPhone:
			typeof record.fallbackPhone === 'string'
				? record.fallbackPhone
				: undefined,
	})
}

function ensureRecallHistory(
	value: RecallPreparednessFormValues['recallHistory'] | undefined,
): RecallPreparednessFormValues['recallHistory'] {
	const status =
		value && isRecallHistoryStatusValue((value as { status?: unknown }).status)
			? (value as { status: RecallHistoryStatusValue }).status
			: DEFAULT_VALUES.recallHistory.status
	const rawEvents =
		value &&
		typeof value === 'object' &&
		Array.isArray((value as { events?: unknown[] }).events)
			? (value as { events: unknown[] }).events
			: []
	const events = rawEvents
		.map((event) => normalizeRecallEventDraft(event))
		.filter((event): event is RecallEventDraft => event !== null)
		.map((event) =>
			prepareRecallEventForStatus(status, event),
		) as RecallEventEntry[]
	return {
		status,
		events: status === 'none' ? [] : events,
	}
}

function normalizeRecallPreparednessDraft(
	value: unknown,
): RecallPreparednessFormValues | null {
	if (!value || typeof value !== 'object') return null
	const record = value as Record<string, unknown>
	const salesChannels = Array.isArray(record.salesChannels)
		? record.salesChannels
				.map((channel) => salesChannelEntryToDraftLike(channel))
				.filter(
					(
						entry,
					): entry is RecallPreparednessFormValues['salesChannels'][number] =>
						entry !== null,
				)
		: DEFAULT_VALUES.salesChannels
	const distributionSystems = Array.isArray(record.distributionSystems)
		? record.distributionSystems
				.map((system) => distributionSystemEntryToDraftLike(system))
				.filter(
					(
						entry,
					): entry is RecallPreparednessFormValues['distributionSystems'][number] =>
						entry !== null,
				)
		: DEFAULT_VALUES.distributionSystems
	const incidentTeam = ensureIncidentTeam(
		record.incidentTeam as IncidentTeamValues | undefined,
	)
	const primaryCoordinatorRaw = normalizeIncidentContact(
		record.primaryCoordinator,
	)
	const primaryCoordinator =
		primaryCoordinatorRaw ?? createEmptyContactReference()
	const drillRecency =
		typeof record.drillRecency === 'string' &&
		RECALL_DRILL_RECENCY_OPTIONS_SET.has(record.drillRecency)
			? record.drillRecency
			: DEFAULT_VALUES.drillRecency
	const recallHistory = ensureRecallHistory(
		record.recallHistory as RecallPreparednessFormValues['recallHistory'],
	)
	const candidate = {
		salesChannels,
		distributionSystems,
		incidentTeam,
		primaryCoordinator,
		drillRecency,
		recallHistory,
	}
	const parsed = RecallPreparednessSchema.safeParse(candidate)
	if (parsed.success) return parsed.data
	console.warn('normalizeRecallPreparednessDraft failed', {
		candidate,
		issues: parsed.error.flatten(),
	})
	return null
}

function recallPreparednessValuesEqual(
	left: RecallPreparednessFormValues,
	right: RecallPreparednessFormValues,
): boolean {
	return JSON.stringify(left) === JSON.stringify(right)
}

function salesChannelEntryToDraftLike(
	input: unknown,
): RecallPreparednessFormValues['salesChannels'][number] | null {
	if (!input || typeof input !== 'object') return null
	const record = input as Record<string, unknown>
	const type = isSalesChannelTypeValue(record.type) ? record.type : undefined
	const draft = sanitizeSalesChannelDraft(
		createSalesChannelDraft({
			type,
			name: typeof record.name === 'string' ? record.name : '',
		}),
	)
	return salesChannelDraftToEntry(draft)
}

function distributionSystemEntryToDraftLike(
	input: unknown,
): RecallPreparednessFormValues['distributionSystems'][number] | null {
	if (!input || typeof input !== 'object') return null
	const record = input as Record<string, unknown>
	const system = isDistributionSystemValue(record.system)
		? record.system
		: undefined
	const draft = sanitizeDistributionSystemDraft(
		createDistributionSystemDraft({
			system,
			dataset: typeof record.dataset === 'string' ? record.dataset : '',
		}),
	)
	return distributionSystemDraftToEntry(draft)
}

function isRecallHistoryStatusValue(
	value: unknown,
): value is RecallPreparednessFormValues['recallHistory']['status'] {
	return (
		typeof value === 'string' && RECALL_HISTORY_STATUS_OPTIONS_SET.has(value)
	)
}
